/**
 * Errors — scrolling log of everything `installErrorCapture()` plus the
 * dev-panel ix runners have pushed. Each row has its own Copy button; a
 * "Copy all" packs the visible (filtered) entries into a single block
 * for paste-into-bug-report.
 *
 * Filter: substring match against `source` and `message`. Most useful as
 * "rpc", "anchor", "kothTouch", etc.
 */
import React, { useMemo, useState } from "react";

import { useDevErrorLog } from "../store/devErrorLog";
import { btnStyle, colors, copyToClipboard, fmtRelTime, styles } from "./_shared";

export function ErrorsSection(): JSX.Element {
  const entries = useDevErrorLog((s) => s.entries);
  const clear = useDevErrorLog((s) => s.clear);

  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const arr = q
      ? entries.filter(
          (e) => e.source.toLowerCase().includes(q) || e.message.toLowerCase().includes(q),
        )
      : entries;
    return [...arr].reverse(); // newest first
  }, [entries, filter]);

  const copyAll = async () => {
    const text = filtered.map((e) => e.copyText).join("\n\n---\n\n");
    await copyToClipboard(text);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.sectionHeader}>
        <div>
          <div style={styles.sectionTitle}>
            Errors ({filtered.length}
            {filter ? ` of ${entries.length}` : ""})
          </div>
          <div style={styles.sectionSubtitle}>
            console.error · unhandledrejection · explicit pushDevError · ring buffer of 200
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter (source or message substring)"
            style={{ ...styles.inputStyle, flex: "0 0 240px" }}
          />
          <button onClick={() => void copyAll()} style={btnStyle({ tone: "ghost", small: true })} disabled={filtered.length === 0}>
            Copy all
          </button>
          <button onClick={clear} style={btnStyle({ tone: "danger", small: true })} disabled={entries.length === 0}>
            Clear
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0 0 12px" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 12, ...styles.mono, color: colors.textMute }}>
            {entries.length === 0 ? "(no errors yet)" : "(filter hides all entries)"}
          </div>
        ) : (
          filtered.map((e) => {
            const isExpanded = expanded[e.id] ?? false;
            return (
              <div key={e.id} style={errorCardStyle}>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                  <span style={{ color: colors.textFaint, fontSize: 10, flex: "0 0 80px" }}>
                    {fmtRelTime(e.ts)}
                  </span>
                  <span style={{ color: colors.fail, fontSize: 10, fontWeight: 700, flex: "0 0 160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {e.source}
                  </span>
                  <span style={{ ...styles.mono, flex: 1, color: colors.text, whiteSpace: "pre-wrap" }}>
                    {isExpanded || e.message.length <= 200 ? e.message : `${e.message.slice(0, 200)}…`}
                  </span>
                  <div style={{ display: "flex", gap: 4 }}>
                    {e.message.length > 200 && (
                      <button
                        onClick={() =>
                          setExpanded((x) => ({ ...x, [e.id]: !x[e.id] }))
                        }
                        style={btnStyle({ tone: "ghost", small: true })}
                      >
                        {isExpanded ? "Collapse" : "Expand"}
                      </button>
                    )}
                    <button
                      onClick={() => void copyToClipboard(e.copyText)}
                      style={btnStyle({ tone: "ghost", small: true })}
                    >
                      Copy
                    </button>
                  </div>
                </div>
                {e.stack && isExpanded && (
                  <pre style={stackStyle}>{e.stack}</pre>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const errorCardStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderBottom: `1px solid ${colors.border}`,
  background: colors.bgRaised,
};

const stackStyle: React.CSSProperties = {
  marginTop: 6,
  padding: 8,
  background: colors.bg,
  border: `1px solid ${colors.border}`,
  borderRadius: 4,
  color: colors.mono,
  fontSize: 10,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-all",
  maxHeight: 240,
  overflowY: "auto",
};
